import React, { useState, useEffect } from 'react';
import { Calendar, FileText, UserCheck, Clock, ChevronRight, CheckCircle, XCircle, Video, MapPin, ArrowLeft, Download, ExternalLink, Users, MessageSquare } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { User } from '../types';
import { getDashboard, batchReview } from '../api';
import { formatBeijingTime } from '../utils/dateFormat';

interface Props {
  currentUser: User;
  notificationBell?: React.ReactNode;
  onNavigateCandidate?: (id: string) => void;
  onOpenCandidate?: (id: string, candidateIds?: string[]) => void;
  onNavigateToOnboarding?: () => void;
  refreshKey?: number;
}

export default function HomePage({ currentUser, notificationBell, onNavigateCandidate, onOpenCandidate, onNavigateToOnboarding, refreshKey }: Props) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showReviewPage, setShowReviewPage] = useState(false);
  const [selectedTaskIds, setSelectedTaskIds] = useState<Set<string>>(new Set());
  const [batchProcessing, setBatchProcessing] = useState(false);

  const refreshData = () => {
    setLoading(true);
    getDashboard().then(d => setData(d)).catch(() => {}).finally(() => setLoading(false));
  };

  const refreshDataSilent = () => {
    getDashboard().then(d => setData(d)).catch(() => {});
  };

  useEffect(() => { refreshData(); }, []);

  useEffect(() => {
    if (refreshKey && refreshKey > 0) refreshDataSilent();
  }, [refreshKey]);

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return '上午好';
    if (h < 18) return '下午好';
    return '晚上好';
  };

  const pendingReviews = data?.pending_reviews || [];
  const pendingEvaluations = data?.pending_evaluations || [];
  const weekInterviews = data?.week_interviews || [];
  const todayCount = data?.today_interview_count || 0;
  const pendingOnboard = data?.pending_onboard || [];
  const [showEvalPage, setShowEvalPage] = useState(false);

  // Group interviews by date
  const interviewsByDate: Record<string, any[]> = {};
  weekInterviews.forEach((iv: any) => {
    const date = (iv.scheduled_time || '').slice(0, 10);
    if (!interviewsByDate[date]) interviewsByDate[date] = [];
    interviewsByDate[date].push(iv);
  });

  const today = new Date();
  const bjToday = new Date(today.getTime() + 8 * 3600 * 1000).toISOString().slice(0, 10);

  const dayLabels: Record<number, string> = { 0: '周日', 1: '周一', 2: '周二', 3: '周三', 4: '周四', 5: '周五', 6: '周六' };
  function getDayLabel(dateStr: string) {
    const d = new Date(dateStr + 'T00:00:00+08:00');
    const label = dayLabels[d.getDay()] || '';
    if (dateStr === bjToday) return `今天 (${label})`;
    return `${dateStr.slice(5)} ${label}`;
  }

  const toggleTask = (id: string) => {
    setSelectedTaskIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedTaskIds.size === pendingReviews.length) {
      setSelectedTaskIds(new Set());
    } else {
      setSelectedTaskIds(new Set(pendingReviews.map((r: any) => r.id)));
    }
  };

  const handleBatchAction = async (result: 'passed' | 'rejected') => {
    const ids = Array.from(selectedTaskIds);
    if (ids.length === 0) { toast.error('请先选择简历'); return; }
    const label = result === 'passed' ? '通过' : '淘汰';
    if (!window.confirm(`确认将 ${ids.length} 份简历标记为「${label}」？`)) return;
    try {
      setBatchProcessing(true);
      const res = await batchReview(ids, result);
      toast.success(res.message || `已处理 ${ids.length} 份`);
      setSelectedTaskIds(new Set());
      refreshDataSilent();
    } catch { toast.error('批量处理失败'); } finally { setBatchProcessing(false); }
  };

  // ===== Batch Review Page =====
  if (showReviewPage) {
    return (
      <div className="h-full flex flex-col bg-gray-50">
        <div className="shrink-0 bg-white border-b border-gray-200 px-4 md:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => { setShowReviewPage(false); setSelectedTaskIds(new Set()); }}
              className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
              <ArrowLeft size={18} className="text-gray-500" />
            </button>
            <div>
              <h1 className="text-base font-bold text-gray-800">待评审简历</h1>
              <p className="text-xs text-gray-400">{pendingReviews.length} 份待处理</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {selectedTaskIds.size > 0 && (
              <>
                <span className="text-xs text-gray-500">已选 {selectedTaskIds.size} 份</span>
                <button onClick={() => handleBatchAction('passed')} disabled={batchProcessing}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50">
                  <CheckCircle size={12} className="inline mr-1" />批量通过
                </button>
                <button onClick={() => handleBatchAction('rejected')} disabled={batchProcessing}
                  className="px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-50">
                  <XCircle size={12} className="inline mr-1" />批量淘汰
                </button>
              </>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Select all bar */}
          <div className="sticky top-0 bg-gray-50 px-4 md:px-6 py-2 flex items-center gap-3 border-b border-gray-100 z-10">
            <label className="flex items-center gap-2 cursor-pointer text-xs text-gray-500">
              <input type="checkbox" checked={selectedTaskIds.size === pendingReviews.length && pendingReviews.length > 0}
                onChange={toggleAll} className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
              全选
            </label>
          </div>

          {pendingReviews.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400">
              <FileText size={40} strokeWidth={1} className="text-gray-300 mb-3" />
              <p className="text-sm">暂无待评审简历</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {pendingReviews.map((r: any) => (
                <div key={r.id} className="flex items-center gap-3 px-4 md:px-6 py-3 hover:bg-white transition-colors">
                  <input type="checkbox" checked={selectedTaskIds.has(r.id)}
                    onChange={() => toggleTask(r.id)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 shrink-0" />
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => {
                      const cIds = pendingReviews.map((t: any) => t.candidate_id);
                      onOpenCandidate?.(r.candidate_id, cIds);
                    }}>
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-[11px] font-bold text-blue-600 shrink-0">
                        {(r.candidate_name || '?').charAt(0)}
                      </div>
                      <span className="text-sm font-medium text-gray-800">{r.candidate_name}</span>
                      {r.position_name && (
                        <span className="text-[10px] text-primary-600 bg-primary-50 px-1.5 py-0.5 rounded-full">{r.position_name}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-[11px] text-gray-400 mt-1 ml-9">
                      {r.school && <span>{r.school}</span>}
                      {r.education && <span>· {r.education}</span>}
                      {r.latest_company && <span>· {r.latest_company}</span>}
                      {r.latest_title && <span>· {r.latest_title}</span>}
                      {r.source && <span>· {r.source}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {r.resume_file && (
                      <a href={r.resume_file} target="_blank" rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                        className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-primary-600"
                        title="查看简历">
                        <Download size={14} />
                      </a>
                    )}
                    <button onClick={(e) => { e.stopPropagation(); handleBatchAction_single(r.id, 'passed'); }}
                      className="p-1.5 rounded-lg hover:bg-green-50 transition-colors text-gray-300 hover:text-green-600" title="通过">
                      <CheckCircle size={16} />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); handleBatchAction_single(r.id, 'rejected'); }}
                      className="p-1.5 rounded-lg hover:bg-red-50 transition-colors text-gray-300 hover:text-red-500" title="淘汰">
                      <XCircle size={16} />
                    </button>
                  </div>
                  <div className="text-[10px] text-gray-400 shrink-0 w-16 text-right">
                    {r.created_by_name && <span>{r.created_by_name}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  async function handleBatchAction_single(taskId: string, result: 'passed' | 'rejected') {
    const label = result === 'passed' ? '通过' : '淘汰';
    try {
      await batchReview([taskId], result);
      toast.success(`已标记为${label}`);
      refreshDataSilent();
    } catch { toast.error('处理失败'); }
  }

  // ===== Pending Evaluations Page =====
  if (showEvalPage) {
    return (
      <div className="h-full flex flex-col bg-gray-50">
        <div className="shrink-0 bg-white border-b border-gray-200 px-4 md:px-6 py-3 flex items-center gap-3">
          <button onClick={() => setShowEvalPage(false)}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
            <ArrowLeft size={18} className="text-gray-500" />
          </button>
          <div>
            <h1 className="text-base font-bold text-gray-800">待处理面试评价</h1>
            <p className="text-xs text-gray-400">{pendingEvaluations.length} 场待填写</p>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {pendingEvaluations.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400">
              <MessageSquare size={40} strokeWidth={1} className="text-gray-300 mb-3" />
              <p className="text-sm">所有面试评价已完成</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {pendingEvaluations.map((ev: any) => (
                <div key={ev.interview_id}
                  className="flex items-center gap-3.5 px-4 md:px-6 py-3.5 hover:bg-white cursor-pointer transition-colors"
                  onClick={() => onOpenCandidate?.(ev.candidate_id)}>
                  <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center text-xs font-bold text-amber-600 shrink-0">
                    {(ev.candidate_name || '?').charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-800">{ev.candidate_name}</span>
                      <span className={clsx('text-[10px] px-1.5 py-0.5 rounded font-medium',
                        ev.round?.includes('终') ? 'bg-orange-50 text-orange-600' :
                        ev.round?.includes('二') ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-500'
                      )}>{ev.round}</span>
                      {ev.position_name && (
                        <span className="text-[10px] text-primary-600 bg-primary-50 px-1.5 py-0.5 rounded-full">{ev.position_name}</span>
                      )}
                    </div>
                    <div className="text-[11px] text-gray-400 mt-1">
                      面试时间: {(ev.scheduled_time || '').replace('T', ' ').slice(0, 16)}
                      {ev.iv_status === 'completed' ? <span className="ml-2 text-green-600">已结束</span>
                        : <span className="ml-2 text-amber-600">已过期未填写</span>}
                    </div>
                  </div>
                  <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-lg font-medium shrink-0">去评价</span>
                  <ChevronRight size={14} className="text-gray-300 shrink-0" />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ===== Main Dashboard =====
  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="shrink-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-gray-800">{greeting()}，{currentUser.display_name || currentUser.username}</h1>
          <p className="text-sm text-gray-500 mt-0.5">这是你的待办事项概览</p>
        </div>
        {notificationBell}
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent" />
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-5">
          {/* Quick stat cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div onClick={() => pendingReviews.length > 0 && setShowReviewPage(true)}
              className={clsx('bg-white rounded-xl border border-gray-200 p-4 transition-colors',
                pendingReviews.length > 0 && 'cursor-pointer hover:border-blue-300 hover:shadow-sm')}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                  <FileText size={20} className="text-blue-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-2xl font-bold text-gray-800">{pendingReviews.length}</p>
                  <p className="text-xs text-gray-500">待评审简历</p>
                </div>
                {pendingReviews.length > 0 && <ChevronRight size={14} className="text-gray-300 hidden md:block" />}
              </div>
            </div>
            <div onClick={() => pendingEvaluations.length > 0 && setShowEvalPage(true)}
              className={clsx('bg-white rounded-xl border border-gray-200 p-4 transition-colors',
                pendingEvaluations.length > 0 && 'cursor-pointer hover:border-amber-300 hover:shadow-sm')}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
                  <MessageSquare size={20} className="text-amber-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-2xl font-bold text-gray-800">{pendingEvaluations.length}</p>
                  <p className="text-xs text-gray-500">待处理评价</p>
                </div>
                {pendingEvaluations.length > 0 && <ChevronRight size={14} className="text-gray-300 hidden md:block" />}
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center shrink-0">
                  <Calendar size={20} className="text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">{todayCount}</p>
                  <p className="text-xs text-gray-500">今日面试</p>
                </div>
              </div>
            </div>
            <div onClick={() => pendingOnboard.length > 0 && onNavigateToOnboarding?.()}
              className={clsx('bg-white rounded-xl border border-gray-200 p-4 transition-colors',
                pendingOnboard.length > 0 && 'cursor-pointer hover:border-teal-300 hover:shadow-sm')}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center shrink-0">
                  <UserCheck size={20} className="text-teal-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-2xl font-bold text-gray-800">{pendingOnboard.length}</p>
                  <p className="text-xs text-gray-500">待入职</p>
                </div>
                {pendingOnboard.length > 0 && <ChevronRight size={14} className="text-gray-300 hidden md:block" />}
              </div>
            </div>
          </div>

          {/* ===== Interview Schedule Section ===== */}
          <div className="bg-white rounded-xl border border-gray-200">
            <div className="px-5 pt-4 pb-3">
              <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <Calendar size={16} className="text-green-500" />
                本周面试日程
                <span className="text-xs font-normal text-gray-400">{weekInterviews.length} 场</span>
              </h2>
            </div>
            {weekInterviews.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-gray-400">
                <Clock size={32} strokeWidth={1} className="text-gray-300 mb-2" />
                <p className="text-sm">本周暂无面试安排</p>
              </div>
            ) : (
              <div className="pb-3">
                {Object.entries(interviewsByDate).map(([date, interviews]) => (
                  <div key={date}>
                    <div className={clsx('px-5 py-2 text-xs font-semibold flex items-center gap-2',
                      date === bjToday ? 'text-green-600 bg-green-50/70' : 'text-gray-500 bg-gray-50/70')}>
                      {date === bjToday && <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />}
                      {getDayLabel(date)}
                      <span className="font-normal text-gray-400">{interviews.length} 场</span>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {interviews.map((iv: any) => {
                        const hasLink = iv.location && /^https?:\/\//.test(iv.location);
                        return (
                        <div key={iv.id} className="flex items-start gap-3.5 px-5 py-3 hover:bg-gray-50/50 transition-colors">
                          <div className="shrink-0 pt-0.5 w-14 text-center">
                            <div className="text-sm font-semibold text-gray-700 font-mono">{(iv.scheduled_time || '').slice(11, 16)}</div>
                            <div className="text-[10px] text-gray-400">{iv.duration_minutes}min</div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap cursor-pointer" onClick={() => onNavigateCandidate?.(iv.candidate_id)}>
                              <span className="text-[13px] font-bold text-gray-800">{iv.candidate_name}</span>
                              <span className={clsx('text-[10px] px-1.5 py-0.5 rounded font-medium',
                                iv.round?.includes('终') ? 'bg-orange-50 text-orange-600' :
                                iv.round?.includes('二') ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-500'
                              )}>{iv.round}</span>
                              {iv.position_name && (
                                <span className="text-[10px] text-primary-600 bg-primary-50 px-1.5 py-0.5 rounded-full">{iv.position_name}</span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 mt-1.5 text-[11px] text-gray-400">
                              {iv.interview_type === 'video' ? (
                                hasLink ? (
                                  <a href={iv.location} target="_blank" rel="noopener noreferrer"
                                    onClick={e => e.stopPropagation()}
                                    className="inline-flex items-center gap-1 text-blue-500 hover:text-blue-600 hover:underline">
                                    <Video size={11} /><span>视频面试</span><ExternalLink size={9} />
                                  </a>
                                ) : (
                                  <span className="inline-flex items-center gap-1"><Video size={11} />视频面试</span>
                                )
                              ) : (
                                <span className="inline-flex items-center gap-1"><MapPin size={11} />{iv.location || '现场面试'}</span>
                              )}
                              {iv.interviewer_names?.length > 0 && (
                                <span className="inline-flex items-center gap-1"><Users size={11} />{iv.interviewer_names.join('、')}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ===== Pending Evaluations Section ===== */}
          {pendingEvaluations.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200">
              <div className="px-5 pt-4 pb-3 flex items-center justify-between">
                <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <MessageSquare size={16} className="text-amber-500" />
                  待处理面试评价
                  <span className="text-xs font-normal text-gray-400">{pendingEvaluations.length} 场</span>
                </h2>
                <button onClick={() => setShowEvalPage(true)}
                  className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1">
                  全部查看 <ChevronRight size={12} />
                </button>
              </div>
              <div className="divide-y divide-gray-100 pb-2">
                {pendingEvaluations.slice(0, 5).map((ev: any) => (
                  <div key={ev.interview_id}
                    className="flex items-center gap-3.5 px-5 py-3 hover:bg-gray-50/50 cursor-pointer transition-colors"
                    onClick={() => onOpenCandidate?.(ev.candidate_id)}>
                    <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-[11px] font-bold text-amber-600 shrink-0">
                      {(ev.candidate_name || '?').charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[13px] font-bold text-gray-800">{ev.candidate_name}</span>
                        <span className={clsx('text-[10px] px-1.5 py-0.5 rounded font-medium',
                          ev.round?.includes('终') ? 'bg-orange-50 text-orange-600' :
                          ev.round?.includes('二') ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-500'
                        )}>{ev.round}</span>
                        {ev.position_name && (
                          <span className="text-[10px] text-primary-600 bg-primary-50 px-1.5 py-0.5 rounded-full">{ev.position_name}</span>
                        )}
                      </div>
                    </div>
                    <span className="text-[11px] text-amber-600 font-medium shrink-0">去评价 →</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ===== Pending Onboard Section ===== */}
          <div className="bg-white rounded-xl border border-gray-200">
            <div className="px-5 pt-4 pb-3 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <UserCheck size={16} className="text-teal-500" />
                待入职
                <span className="text-xs font-normal text-gray-400">{pendingOnboard.length} 人</span>
              </h2>
              {pendingOnboard.length > 0 && onNavigateToOnboarding && (
                <button onClick={onNavigateToOnboarding}
                  className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1">
                  管理入职流程 <ChevronRight size={12} />
                </button>
              )}
            </div>
            {pendingOnboard.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-gray-400">
                <UserCheck size={32} strokeWidth={1} className="text-gray-300 mb-2" />
                <p className="text-sm">暂无待入职候选人</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100 pb-2">
                {pendingOnboard.map((c: any) => {
                  const infoParts: string[] = [];
                  if (c.school) infoParts.push(c.school);
                  if (c.education) infoParts.push(c.education);
                  if (c.latest_company) infoParts.push(c.latest_company);
                  if (c.expected_city) infoParts.push(c.expected_city);
                  return (
                    <div key={c.id}
                      onClick={() => onNavigateCandidate?.(c.id)}
                      className="flex items-center gap-3.5 px-5 py-3.5 hover:bg-gray-50/50 cursor-pointer transition-colors"
                    >
                      <div className="w-9 h-9 rounded-full bg-teal-100 flex items-center justify-center text-xs font-bold text-teal-600 shrink-0">
                        {(c.name || '?').charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-[13px] font-bold text-gray-800">{c.name}</span>
                          {c.position_name && (
                            <span className="text-[10px] text-primary-600 bg-primary-50 px-1.5 py-0.5 rounded-full">{c.position_name}</span>
                          )}
                        </div>
                        {infoParts.length > 0 && (
                          <div className="text-[11px] text-gray-400 mt-1 truncate">{infoParts.join(' · ')}</div>
                        )}
                      </div>
                      <div className="text-right shrink-0 space-y-0.5">
                        {c.onboard_date ? (
                          <div className="text-[11px] font-medium text-teal-600 bg-teal-50 px-2 py-0.5 rounded">
                            入职: {c.onboard_date}
                          </div>
                        ) : c.offer_approved_at ? (
                          <div className="text-[11px] text-amber-600 bg-amber-50 px-2 py-0.5 rounded">
                            待定入职时间
                          </div>
                        ) : null}
                        {c.offer_approved_at && (
                          <div className="text-[10px] text-gray-400">Offer通过: {formatBeijingTime(c.offer_approved_at, 'date')}</div>
                        )}
                      </div>
                      <ChevronRight size={14} className="text-gray-300 shrink-0" />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
